<template>
  <Dialog v-model:visible="showModal" class="w-4/5 lg:w-2/5 bg-white shadow rounded-xl" :modal="true" :closable="false">
    <template #header>
      <div class="w-full text-gray-900 font-semibold">{{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['title'] || '發票資訊' }}</div>
    </template>

    <div class="space-y-4" style="padding: 32px;">
      <p class="text-gray-500 text-sm mb-4">{{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['description'] || '請填寫發票資訊，付款成功後將開立電子發票' }}</p>

      <!-- 統一編號 (選填) -->
      <div class="flex flex-col">
        <label class="text-gray-700 font-medium mb-1">
          {{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['identifier'] || '統一編號' }}
          <span class="text-gray-400 text-sm ml-1">({{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['optional'] || '選填' }})</span>
        </label>
        <input
          type="text"
          v-model="formData.identifier"
          class="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          :placeholder="lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['identifier_placeholder'] || '如需三聯式發票請填寫'"
          maxlength="8"
        />
      </div>

      <!-- 發票抬頭/姓名 (必填) -->
      <div class="flex flex-col">
        <label class="text-gray-700 font-medium mb-1">
          <span class="text-red-500">*</span>
          {{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['name'] || '發票抬頭/姓名' }}
        </label>
        <input
          type="text"
          v-model="formData.name"
          class="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          :class="{ 'border-red-500': errors.name }"
          :placeholder="lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['name_placeholder'] || '請輸入公司名稱或姓名'"
        />
        <span v-if="errors.name" class="text-red-500 text-sm mt-1">{{ errors.name }}</span>
      </div>

      <!-- 地址 (必填) -->
      <div class="flex flex-col">
        <label class="text-gray-700 font-medium mb-1">
          <span class="text-red-500">*</span>
          {{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['address'] || '地址' }}
        </label>
        <input
          type="text"
          v-model="formData.address"
          class="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          :class="{ 'border-red-500': errors.address }"
          :placeholder="lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['address_placeholder'] || '請輸入地址'"
        />
        <span v-if="errors.address" class="text-red-500 text-sm mt-1">{{ errors.address }}</span>
      </div>

      <!-- 手機號碼 (必填) -->
      <div class="flex flex-col">
        <label class="text-gray-700 font-medium mb-1">
          <span class="text-red-500">*</span>
          {{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['mobile'] || '手機號碼' }}
        </label>
        <input
          type="tel"
          v-model="formData.mobile"
          class="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          :class="{ 'border-red-500': errors.mobile }"
          :placeholder="lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['mobile_placeholder'] || '請輸入手機號碼'"
        />
        <span v-if="errors.mobile" class="text-red-500 text-sm mt-1">{{ errors.mobile }}</span>
      </div>

      <!-- Email (必填) -->
      <div class="flex flex-col">
        <label class="text-gray-700 font-medium mb-1">
          <span class="text-red-500">*</span>
          {{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['email'] || 'Email' }}
        </label>
        <input
          type="email"
          v-model="formData.email"
          class="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          :class="{ 'border-red-500': errors.email }"
          :placeholder="lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['email_placeholder'] || '請輸入 Email'"
        />
        <span v-if="errors.email" class="text-red-500 text-sm mt-1">{{ errors.email }}</span>
      </div>
    </div>

    <template #footer>
      <div class="w-full flex justify-end gap-2 py-4 border-t border-gray-200">
        <button
          class="hover:bg-gray-200 focus:outline-none rounded-xl px-4 py-2 text-gray-500 transition duration-300 border border-gray-300"
          @click="onCancel"
        >
          {{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['cancel_button'] || '取消' }}
        </button>
        <button
          class="bg-blue-500 hover:bg-blue-600 focus:outline-none rounded-xl px-4 py-2 text-white transition duration-300"
          :disabled="isSubmitting"
          @click="onSubmit"
        >
          <span v-if="isSubmitting">{{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['submitting'] || '儲存中...' }}</span>
          <span v-else>{{ lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['submit_button'] || '確認並付款' }}</span>
        </button>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import axios from 'axios'
import Qs from 'qs'
import Swal from 'sweetalert2/dist/sweetalert2.js'

export default {
  name: 'InvoiceDialog',
  components: {
    Dialog
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    invoiceData: {
      type: Object,
      default: () => ({
        identifier: '',
        name: '',
        address: '',
        mobile: '',
        email: ''
      })
    },
    lang: {
      type: Object
    }
  },
  emits: ['update:show', 'invoiceSaved'],
  data() {
    return {
      formData: {
        identifier: '',
        name: '',
        address: '',
        mobile: '',
        email: ''
      },
      errors: {
        name: '',
        address: '',
        mobile: '',
        email: ''
      },
      isSubmitting: false
    }
  },
  watch: {
    invoiceData: {
      handler(newVal) {
        if (newVal) {
          this.formData = { ...newVal }
        }
      },
      immediate: true,
      deep: true
    },
    show(newVal) {
      if (newVal && this.invoiceData) {
        this.formData = { ...this.invoiceData }
        this.clearErrors()
      }
    }
  },
  methods: {
    clearErrors() {
      this.errors = {
        name: '',
        address: '',
        mobile: '',
        email: ''
      }
    },
    validate() {
      this.clearErrors()
      let isValid = true

      // 姓名必填
      if (!this.formData.name || !this.formData.name.trim()) {
        this.errors.name = this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_name_required'] || '請輸入發票抬頭/姓名'
        isValid = false
      }

      // 地址必填
      if (!this.formData.address || !this.formData.address.trim()) {
        this.errors.address = this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_address_required'] || '請輸入地址'
        isValid = false
      }

      // 手機必填
      if (!this.formData.mobile || !this.formData.mobile.trim()) {
        this.errors.mobile = this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_mobile_required'] || '請輸入手機號碼'
        isValid = false
      } else if (!/^09\d{8}$/.test(this.formData.mobile.trim())) {
        this.errors.mobile = this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_mobile_format'] || '請輸入正確的手機號碼格式'
        isValid = false
      }

      // Email 必填
      if (!this.formData.email || !this.formData.email.trim()) {
        this.errors.email = this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_email_required'] || '請輸入 Email'
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email.trim())) {
        this.errors.email = this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_email_format'] || '請輸入正確的 Email 格式'
        isValid = false
      }

      // 統一編號格式 (選填，但如果填了要檢查格式)
      if (this.formData.identifier && this.formData.identifier.trim()) {
        if (!/^\d{8}$/.test(this.formData.identifier.trim())) {
          Swal.fire({
            icon: 'warning',
            title: this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_identifier_format_title'] || '統一編號格式錯誤',
            text: this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['error_identifier_format'] || '統一編號應為 8 碼數字'
          })
          isValid = false
        }
      }

      return isValid
    },
    async onSubmit() {
      if (!this.validate()) {
        return
      }

      this.isSubmitting = true

      try {
        const response = await axios.post('https://www.autoturn.net/api/data/invoices_update.php', Qs.stringify({
          s_code: this.$route.params.id,
          form_data: this.formData,
          type: this.$route.params.type === 'tiktok' ? 1 : 0
        }))

        if (response.data.status === 'success') {
          this.$emit('invoiceSaved')
        } else {
          Swal.fire({
            icon: 'error',
            title: this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['save_error_title'] || '儲存失敗',
            text: response.data.info || this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['save_error_text'] || '發票資訊儲存失敗，請稍後再試'
          })
        }
      } catch (error) {
        console.error('儲存發票資訊失敗:', error)
        Swal.fire({
          icon: 'error',
          title: this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['save_error_title'] || '儲存失敗',
          text: this.lang?.['components']?.['Dialog']?.['InvoiceDialog']?.['network_error'] || '網路連線失敗，請稍後再試'
        })
      } finally {
        this.isSubmitting = false
      }
    },
    onCancel() {
      this.showModal = false
    }
  },
  computed: {
    showModal: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('update:show', value)
      }
    }
  }
}
</script>

<style scoped>
input:focus {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}
</style>
